import { Item, Variant } from "@aitisal/contracts/restaurant/menu";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { useRouter } from "@tanstack/react-router";
import { ArrowLeft, Check, Heart, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import ProductGallery from "../components/retail/ProductGallery";
import { ProductDetailSkeleton } from "../components/retail/RetailSkeletons";
import { getTitle } from "../helper/helper";
import { useMenuAggregate } from "../hooks/useMenuAggregate";
import {
  mapProductData,
  useProductPurchase,
  type ProductData,
} from "../hooks/useProductPurchase";

/**
 * Default product details page. Fetches through the same hooks/query keys as
 * every other theme and renders presentation-only over useProductPurchase —
 * the money path is never duplicated (§7).
 */
export default function ProductDetails({ itemId }: { itemId: string }) {
  const [item, setItem] = useState<Item>();
  const router = useRouter();

  const { data: menusData, isLoading } = useMenuAggregate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const resetScroll = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
    return () => window.clearTimeout(resetScroll);
  }, [itemId]);

  useEffect(() => {
    const allItems = menusData?.menus?.flatMap((m: any) => m.sections?.flatMap((s: any) => s.items || []) || []) || [];
    const currentitem = allItems.find((i: Item) => i.itemId === itemId);
    setItem(currentitem);
  }, [menusData, itemId]);

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-gray-500">
        <p className="text-lg font-semibold">Product not found</p>
        <Button variant="outline" onClick={() => router.history.back()}>
          Back
        </Button>
      </div>
    );
  }

  const mappedProduct: ProductData = mapProductData(item);

  return (
    <div className="min-h-screen bg-white">
      <ProductDisplay
        product={mappedProduct}
        attributes={item?.attributes ?? []}
        variants={item?.variants ?? []}
      />
    </div>
  );
}

interface ProductDisplayProps {
  product: ProductData;
  attributes?: { attributeId: string; name: any; options?: any[] }[];
  variants?: Variant[];
}

function ProductDisplay({
  product,
  attributes = [],
  variants = [],
}: ProductDisplayProps) {
  const {
    quantity,
    isAdding,
    customizations,
    isWishlisted,
    selectedOptionByAxis,
    setSelectedOptionByAxis,
    isVariantItem,
    isVariantIncomplete,
    canAddToCart,
    activePriceCents,
    activeCompareAtCents,
    showCompareAt,
    discountPercent,
    toggleWishlist,
    handleCustomizationOptionSelect,
    handleOptionQuantityChange,
    handleQuantityChange,
    handleAddToCart,
    handleBuyNow,
    formatPrice,
  } = useProductPurchase({ product, attributes, variants });

  const router = useRouter();

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => router.history.back()}
        className="h-auto w-auto justify-start gap-2 rounded-none bg-transparent px-0 text-gray-600 hover:bg-transparent hover:text-gray-900 mb-6 transition-colors cursor-pointer"
      >
        <ArrowLeft className="h-5 w-5" />
        <span className="text-sm font-medium">Back</span>
      </Button>

      <div className="flex flex-col lg:flex-row lg:gap-8">
        {/* Left Side: Product Gallery */}
        <div className="flex-1">
          <ProductGallery
            images={product.rawItem.images ?? []}
            title={product.title}
          />
        </div>

        {/* Right Side: Product Details */}
        <div className="flex-1 mt-6 lg:mt-0 lg:pl-8">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight pr-2 sm:pr-4">
              {product.title}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleWishlist}
              className={`text-gray-400 hover:text-red-500 shrink-0 h-12 w-12 ${isWishlisted ? "text-red-500" : ""}`}
            >
              <Heart
                className={`h-7 w-7 sm:h-8 sm:w-8 ${isWishlisted ? "fill-current" : ""}`}
              />
            </Button>
          </div>

          <div className="flex items-baseline gap-3 flex-wrap mb-6">
            <p
              className="text-4xl font-bold"
              style={
                showCompareAt
                  ? { color: "var(--discounted-price-text-color, #f44336)" }
                  : undefined
              }
            >
              Rs. {formatPrice(activePriceCents / 100)}
            </p>
            {showCompareAt && activeCompareAtCents != null && (
              <span className="text-xl font-medium text-gray-400 line-through">
                Rs. {formatPrice(activeCompareAtCents / 100)}
              </span>
            )}
            {discountPercent >= 1 && (
              <span
                className="text-xs font-bold px-2 py-1 rounded-md"
                style={{
                  color: "var(--discounted-price-text-color, #f44336)",
                  backgroundColor:
                    "color-mix(in srgb, var(--discounted-price-text-color, #f44336) 12%, transparent)",
                }}
              >
                -{discountPercent}%
              </span>
            )}
          </div>

          <p className="text-gray-600 mb-8 leading-relaxed">
            {product.description}
          </p>

          {/* Variant Picker Section */}
          {isVariantItem && (
            <div className="mb-8">
              <div className="border-t border-gray-200">
                {attributes.map((axis) => {
                  const axisName = getTitle(axis.name);
                  const options = axis.options ?? [];
                  const selectedOptionId = selectedOptionByAxis[axis.attributeId];
                  return (
                    <div
                      key={axis.attributeId}
                      className="py-5 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between gap-3 mb-3.5">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <h3 className="text-[15px] font-semibold text-gray-900 tracking-tight">
                            {axisName}
                          </h3>
                          <span className="text-[11px] font-medium uppercase tracking-wider text-gray-900">
                            Required
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2.5">
                        {options.map((opt: any) => {
                          const optName = getTitle(opt.name);
                          const isSelected = selectedOptionId === opt.attributeOptionId;
                          return (
                            <Button
                              variant="ghost"
                              key={opt.attributeOptionId}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => {
                                setSelectedOptionByAxis((prev) => ({
                                  ...prev,
                                  [axis.attributeId]: opt.attributeOptionId,
                                }));
                              }}
                              className={`
                                group min-h-[2.5rem] min-w-[3rem] h-10 w-auto px-4
                                rounded-xl text-sm font-medium
                                border transition-all duration-200
                                flex items-center justify-center
                                ${
                                  isSelected
                                    ? "bg-[var(--brand-color)] hover:bg-[var(--brand-color)] text-white border-[var(--brand-color)]"
                                    : "bg-white hover:bg-white text-gray-900 border-gray-300 hover:border-gray-900"
                                }
                              `}
                            >
                              {isSelected && (
                                <Check
                                  className="w-3.5 h-3.5 mr-1.5 -ml-0.5"
                                  strokeWidth={3}
                                />
                              )}
                              <span className="whitespace-nowrap">
                                {optName}
                              </span>
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {isVariantIncomplete && (
                  <p className="mt-2.5 text-[11px] font-medium uppercase tracking-wider text-red-500">
                    Please select an option for each variant.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Customizations Section */}
          {customizations.length > 0 && (
            <div className="mb-8">
              <div className="border-t border-gray-200">
                {customizations.map((customization) => {
                  const isRadioBehavior =
                    customization.maxSelectable === 1 &&
                    !customization.isOptional;
                  const selectedCount = customization.options.filter(
                    (o) => o.isSelected,
                  ).length;
                  const isMaxReached =
                    selectedCount >= customization.maxSelectable;
                  const isMultiSelect =
                    !isRadioBehavior && customization.maxSelectable > 1;

                  const helperText = customization.isOptional
                    ? isRadioBehavior
                      ? "Optional · Choose 1"
                      : `Optional · Choose up to ${customization.maxSelectable}`
                    : isRadioBehavior
                      ? "Required"
                      : `Required · Choose up to ${customization.maxSelectable}`;

                  return (
                    <div
                      key={customization.customizationId}
                      className="py-5 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between gap-3 mb-3.5">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <h3 className="text-[15px] font-semibold text-gray-900 tracking-tight">
                            {customization.name}
                          </h3>
                          <span
                            className={`text-[11px] font-medium uppercase tracking-wider ${
                              customization.isOptional
                                ? "text-gray-400"
                                : "text-gray-900"
                            }`}
                          >
                            {customization.isOptional ? "Optional" : "Required"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                          {isMultiSelect && (
                            <span
                              className={`tabular-nums ${
                                selectedCount > 0 ? "text-gray-900" : ""
                              }`}
                            >
                              {selectedCount}/{customization.maxSelectable}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2.5">
                        {customization.options.map((option) => {
                          const optMin = option.minQuantity ?? 1;
                          const optMax = option.maxQuantity ?? 1;
                          const isQtyConfigurable =
                            !(optMin === 1 && optMax === 1);
                          const optionQty =
                            option.quantity ?? option.minQuantity ?? 1;
                          const isOptionDisabled =
                            !isRadioBehavior &&
                            !option.isSelected &&
                            isMaxReached;
                          const showStepper =
                            option.isSelected && isQtyConfigurable;

                          return (
                            <div
                              key={option.itemId}
                              className="flex items-center gap-2"
                            >
                              <Button
                                variant="ghost"
                                type="button"
                                disabled={isOptionDisabled}
                                aria-pressed={option.isSelected}
                                onClick={() => {
                                  if (isOptionDisabled) return;
                                  handleCustomizationOptionSelect(
                                    customization.customizationId,
                                    option.itemId,
                                  );
                                }}
                                className={`
                                  group min-h-[2.5rem] min-w-[3rem] h-10 w-auto px-4
                                  rounded-xl text-sm font-medium
                                  border transition-all duration-200
                                  flex items-center justify-center
                                  ${
                                    option.isSelected
                                      ? "bg-[var(--brand-color)] hover:bg-[var(--brand-color)] text-white border-[var(--brand-color)]"
                                      : isOptionDisabled
                                        ? "bg-white hover:bg-white text-gray-400 border-gray-200 cursor-not-allowed opacity-60"
                                        : "bg-white hover:bg-white text-gray-900 border-gray-300 hover:border-gray-900"
                                  }
                                `}
                              >
                                {option.isSelected && (
                                  <Check
                                    className="w-3.5 h-3.5 mr-1.5 -ml-0.5"
                                    strokeWidth={3}
                                  />
                                )}
                                <span className="whitespace-nowrap">
                                  {option.name}
                                </span>
                                {option.price > 0 && (
                                  <span
                                    className={`ml-1.5 text-xs tabular-nums ${
                                      option.isSelected
                                        ? "text-white/70"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    +Rs.{formatPrice(option.price)}
                                  </span>
                                )}
                              </Button>

                              {showStepper && (
                                <div
                                  className="flex items-center h-10 bg-gray-100 rounded-full overflow-hidden"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    type="button"
                                    aria-label="Decrease quantity"
                                    onClick={() =>
                                      handleOptionQuantityChange(
                                        customization.customizationId,
                                        option.itemId,
                                        -1,
                                      )
                                    }
                                    disabled={optionQty <= optMin}
                                    className="w-9 h-full rounded-none bg-transparent text-gray-900 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                                  >
                                    <Minus className="w-3.5 h-3.5" />
                                  </Button>
                                  <span className="w-8 text-center text-sm font-semibold text-gray-900 tabular-nums select-none">
                                    {optionQty}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    type="button"
                                    aria-label="Increase quantity"
                                    onClick={() =>
                                      handleOptionQuantityChange(
                                        customization.customizationId,
                                        option.itemId,
                                        1,
                                      )
                                    }
                                    disabled={optionQty >= optMax}
                                    className="w-9 h-full rounded-none bg-transparent text-gray-900 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <p className="mt-2.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">
                        {helperText}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-row gap-4 mb-6">
            {/* Quantity Selector */}
            <div className="flex items-center justify-between bg-gray-100 rounded-xl px-2 py-1 h-12 w-auto min-w-[140px]">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Decrease quantity"
                onClick={() => handleQuantityChange("decrement")}
                className="w-10 h-10 rounded-lg bg-transparent text-gray-900 hover:bg-white hover:text-gray-900"
              >
                <Minus className="h-5 w-5 text-gray-700" />
              </Button>
              <span className="text-lg font-bold text-gray-900 min-w-[2.5rem] text-center">
                {quantity}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Increase quantity"
                onClick={() => handleQuantityChange("increment")}
                className="w-10 h-10 rounded-lg bg-transparent text-gray-900 hover:bg-white hover:text-gray-900"
              >
                <Plus className="h-5 w-5 text-gray-700" />
              </Button>
            </div>

            <Button
              disabled={isAdding || !canAddToCart}
              onClick={handleAddToCart}
              className={`flex-1 h-12 text-base font-semibold transition-all rounded-xl ${
                !canAddToCart
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-200"
                  : isAdding
                    ? "bg-green-600 text-white"
                    : "bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-50"
              }`}
            >
              {isAdding ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Added to Cart!
                </span>
              ) : !canAddToCart ? (
                "Select Options"
              ) : (
                "Add To Cart"
              )}
            </Button>
          </div>

          <Button
            disabled={!canAddToCart}
            onClick={handleBuyNow}
            className={`w-full h-12 text-base font-semibold rounded-xl mb-6 shadow-lg transition-transform cursor-pointer ${
              !canAddToCart
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[var(--brand-color)] text-white hover:opacity-90"
            }`}
          >
            {!canAddToCart ? "Please Select Options" : "Buy Now"}
          </Button>
        </div>
      </div>
    </div>
  );
}
